import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useT } from '../../i18n/LangProvider';

type QA = { qEn: string; qFr: string; aEn: string; aFr: string };

const FAQS: QA[] = [
  {
    qEn: 'Is attendance required?',
    qFr: "La présence est-elle obligatoire?",
    aEn: "If you've been invited for in-person attendance, you are expected to join the event onsite. Please complete the registration so we can plan accordingly. If you have any accommodations or exceptions to discuss, your direct manager is happy to connect with you before you respond. For our remote teams in North America, we encourage you to join live via Teams throughout the day. We understand time zones may make it difficult for remote teams outside North America to attend live — a recording will be available to watch whenever it works best for you.",
    aFr: "Si vous avez été invité à assister à l'événement en personne, vous êtes attendu sur place. Veuillez compléter votre inscription afin que nous puissions planifier en conséquence. Si vous avez des besoins particuliers ou des exceptions à discuter, votre gestionnaire direct se fera un plaisir d'en discuter avec vous avant que vous répondiez. Pour nos équipes à distance en Amérique du Nord, nous vous encourageons à vous joindre en direct via Teams tout au long de la journée. Nous comprenons que le décalage horaire peut rendre la participation en direct difficile pour les équipes à distance hors Amérique du Nord — un enregistrement sera disponible pour visionnement au moment qui vous convient le mieux."
  },
  {
    qEn: 'What do I do when I arrive at Centre Mont-Royal?',
    qFr: "Que dois-je faire à mon arrivée au Centre Mont-Royal?",
    aEn: 'Please enter the venue via the street-level entrance on Mansfield Street (Level RC). Look for the flags outside. Once inside, check in at the registration desk on Level 1 before finding your spot in the Symposia theatre on Level 1.',
    aFr: "Veuillez entrer par l'entrée au niveau de la rue Mansfield (niveau RC). Repérez les drapeaux à l'extérieur. Une fois à l'intérieur, présentez-vous au comptoir d'inscription au niveau 1 avant de vous rendre à votre place dans la salle Symposia, également au niveau 1."
  },
  {
    qEn: 'Will there be air conditioning?',
    qFr: "Y aura-t-il de la climatisation?",
    aEn: "Yes! And it works! We've made very sure the venue's air conditioning will keep the space comfortable throughout the event.",
    aFr: "Oui! Et elle fonctionne! Nous nous sommes assurés que la climatisation du lieu maintiendra un environnement confortable tout au long de l'événement."
  },
  {
    qEn: 'Will there be parking onsite?',
    qFr: "Le stationnement sur place sera-t-il disponible?",
    aEn: 'Yes, parking is available onsite (limited availability, spots not guaranteed) for $20/day at your own expense. Public transportation is encouraged. Street parking is also available at your own expense.',
    aFr: "Oui, le stationnement sur place est disponible (places limitées, non garanties) au coût de 20 $ par jour, à vos frais. Le transport en commun est encouragé. Le stationnement dans la rue est également disponible, à vos frais."
  },
  {
    qEn: 'Is there a dress code?',
    qFr: "Y a-t-il un code vestimentaire?",
    aEn: 'The dress code is business casual, similar to what you would wear to the office. Please note there will not be a coat check service available.',
    aFr: "La tenue est de type décontracté professionnel, similaire à ce que vous porteriez au bureau. Veuillez noter qu'aucun service de vestiaire ne sera offert."
  },
  {
    qEn: 'How do I connect to WiFi?',
    qFr: "Comment puis-je me connecter au WiFi?",
    aEn: 'We will have a dedicated WiFi connection available onsite. Login details will be shared via email.',
    aFr: "Une connexion WiFi dédiée sera disponible sur place. Les identifiants de connexion vous seront transmis par courriel."
  },
  {
    qEn: 'Is the venue wheelchair accessible?',
    qFr: "Le lieu est-il accessible aux personnes à mobilité réduite?",
    aEn: 'Yes. The venue is fully wheelchair accessible. For any specific accommodations, please contact your HRBP in advance.',
    aFr: "Oui. Le lieu est entièrement accessible aux personnes à mobilité réduite. Pour tout besoin particulier, veuillez communiquer avec votre PARH (partenaire d'affaires en ressources humaines) à l'avance."
  },
  {
    qEn: 'Will photos be taken at the event?',
    qFr: "Des photos seront-elles prises pendant l'événement?",
    aEn: 'We will be taking photos throughout the day. If you prefer not to be photographed, please let us know in advance. These photos may be used to highlight Tecsys events and our people in various communications.',
    aFr: "Des photos seront prises tout au long de la journée. Si vous préférez ne pas être photographié, veuillez nous en aviser à l'avance. Ces photos pourraient être utilisées pour mettre en valeur les événements de Tecsys et nos employés dans diverses communications."
  },
  {
    qEn: 'Can I access the content in other languages?',
    qFr: "Puis-je accéder au contenu dans d'autres langues ?",
    aEn: 'Yes, both virtual and in-person attendees can access the live presentations from Teams and select the language they would like to listen to. In-person attendees who wish to do this should bring a device and a portable charger.',
    aFr: "Oui, les participants virtuels et en personne peuvent accéder aux présentations en direct depuis Teams et choisir la langue d'écoute. Les participants en personne qui souhaitent le faire devraient apporter un appareil et un chargeur portable."
  },
  {
    qEn: "If I'm attending virtually, how can I participate?",
    qFr: "Si je participe virtuellement, comment puis-je m'impliquer ?",
    aEn: 'There will be a live chat in the Teams environment where you can submit questions and comments — and we encourage you to!',
    aFr: "Un clavardage en direct sera disponible dans l'environnement Teams où vous pourrez soumettre vos questions et commentaires — et nous vous y encourageons !"
  },
  {
    qEn: "What if I'm in a time zone that misses most of the live content?",
    qFr: "Et si mon fuseau horaire me fait manquer la majeure partie du contenu en direct ?",
    aEn: "We know Connect brings together people from all over, and the agenda runs at different times depending on where you're located. That's why every session is recorded — so if the timing doesn't work for you live, you can catch the full agenda on demand afterward.",
    aFr: "Nous savons que Connect réunit des personnes de partout, et le programme se déroule à des heures différentes selon votre emplacement. C'est pourquoi chaque séance est enregistrée — si l'horaire ne vous convient pas en direct, vous pourrez visionner tout le programme sur demande par la suite."
  },
  {
    qEn: 'Is there a code I need to use when filing travel and accommodation expenses for Tecsys Connect?',
    qFr: "Y a-t-il un code à utiliser pour les dépenses de voyage et d'hébergement liées à Connexion Tecsys ?",
    aEn: 'Please use Tecsys Connect and GO Event, code number P007265-001, for all expenses related to this event.',
    aFr: "Veuillez utiliser Connexion Tecsys et GO Event, code numéro P007265-001, pour toutes les dépenses liées à cet événement."
  }
];

export default function FaqPage() {
  const { t, lang } = useT();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{t('faq.kicker')}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight text-ink mb-12">{t('faq.title')}</motion.h1>

      <div className="space-y-3">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="card overflow-hidden">
              <button onClick={() => setOpen(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 p-6 text-left">
                <span className="font-display text-lg text-ink">{lang === 'fr' ? f.qFr : f.qEn}</span>
                <motion.span animate={{ rotate: isOpen ? 45 : 0 }} className="text-brand-red text-2xl leading-none flex-shrink-0">+</motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="px-6 pb-6 text-ink-muted leading-relaxed">{lang === 'fr' ? f.aFr : f.aEn}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <p className="text-ink-muted text-sm mt-10">{lang === 'fr' ? 'Des questions? Écrivez-nous à ' : 'Questions? Reach out to '}<a href="mailto:connect@tecsys.com" className="text-brand-red hover:underline">connect@tecsys.com</a></p>
    </div>
  );
}
